import { useEffect, useState } from 'react';

// A touch tap fires its mousedown, which moves focus, and its mouseup after its pointer events end.
const PRESS_EVENTS = ['pointerdown', 'mousedown'] as const;
// A context menu or a drag can swallow the mouseup, so each ends the press too.
const RELEASE_EVENTS = [
  'pointerup',
  'pointercancel',
  'mouseup',
  'contextmenu',
  'dragend',
] as const;

let pressed = false;
let users = 0;
const releaseCallbacks = new Set<() => void>();

const press = (event: Event) => {
  // Only a primary press clicks; `> 0` because jsdom's pointer events carry no button.
  if ((event as MouseEvent).button > 0) return;
  pressed = true;
};

const release = () => {
  pressed = false;
  const callbacks = [...releaseCallbacks];
  releaseCallbacks.clear();
  callbacks.forEach(callback => callback());
};

/** Tracks presses on the page while any hook is mounted; the last one out forgets them. */
const track = () => {
  if (users++ === 0) {
    PRESS_EVENTS.forEach(type => document.addEventListener(type, press, true));
    RELEASE_EVENTS.forEach(type =>
      document.addEventListener(type, release, true),
    );
    // A release in another window never reaches this page. Not capture: an element's blur would count.
    window.addEventListener('blur', release);
  }

  return () => {
    if (--users > 0) return;
    PRESS_EVENTS.forEach(type =>
      document.removeEventListener(type, press, true),
    );
    RELEASE_EVENTS.forEach(type =>
      document.removeEventListener(type, release, true),
    );
    window.removeEventListener('blur', release);
    pressed = false;
    releaseCallbacks.clear();
  };
};

/** Calls `callback` at the next release, or now when nothing is pressed; returns a cancel. */
const onRelease = (callback: () => void) => {
  if (!pressed) {
    callback();
    return () => {};
  }

  releaseCallbacks.add(callback);
  return () => {
    releaseCallbacks.delete(callback);
  };
};

/** `active`, except that turning true while a pointer is pressed shows only in a task after its release. */
export const usePointerReleaseDeferred = (active: boolean): boolean => {
  const [previous, setPrevious] = useState(active);
  const [deferred, setDeferred] = useState(false);

  // Decided while rendering, so a turn with nothing pressed shows in the same commit
  // and a turn during a press never commits.
  if (active !== previous) {
    setPrevious(active);
    setDeferred(active && pressed);
  }

  useEffect(() => track(), []);

  useEffect(() => {
    if (!deferred) return;

    let timer: ReturnType<typeof setTimeout> | undefined;
    // The release's own task still dispatches its click, on the layout that was pressed.
    const cancel = onRelease(() => {
      timer = setTimeout(() => setDeferred(false));
    });

    return () => {
      cancel();
      clearTimeout(timer);
    };
  }, [deferred]);

  return active && !deferred;
};
